import React, { useState, useEffect, useRef } from 'react';
import { api } from '../App';
import { Upload, FileText, Trash2, Tag, Plus } from 'lucide-react';

export default function Documents() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showTextForm, setShowTextForm] = useState(false);
  const [textContent, setTextContent] = useState('');
  const [docName, setDocName] = useState('');
  const [tags, setTags] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadDocuments();
  }, []);

  const loadDocuments = async () => {
    try {
      const res = await api.get('/api/documents');
      setDocuments(res.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('doc_name', file.name);
      formData.append('tags', 'uploaded');
      await api.post('/api/documents/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      loadDocuments();
    } catch (err) {
      console.error(err);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleTextUpload = async () => {
    if (!textContent.trim() || !docName.trim()) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('text_content', textContent);
      formData.append('doc_name', docName);
      formData.append('tags', tags);
      await api.post('/api/documents/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setTextContent('');
      setDocName('');
      setTags('');
      setShowTextForm(false);
      loadDocuments();
    } catch (err) {
      console.error(err);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await api.delete(`/api/documents/${docId}`);
      loadDocuments();
    } catch (err) {
      console.error(err);
    }
  };

  if (loading) return <div className="loading-screen" style={{ height: 400, background: 'transparent' }}><div className="spinner" style={{ borderTopColor: 'var(--panzix-green)' }} /></div>;

  return (
    <div className="documents-page fade-in">
      <div style={{ marginBottom: 24 }}>
        <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 6 }}>Company Documents</h3>
        <p style={{ fontSize: 14, color: '#64748b' }}>Upload company handbooks, policies, and other documents. Your AI employees will use these for context-aware responses.</p>
      </div>

      {/* File Upload */}
      <div className="upload-area" onClick={() => fileInputRef.current?.click()} data-testid="upload-area">
        <Upload size={32} color="#94a3b8" style={{ marginBottom: 8 }} />
        <h4>{uploading ? 'Uploading...' : 'Click to upload a document'}</h4>
        <p>Supports PDF, Word (.docx), Excel (.xlsx), PowerPoint (.pptx), images, and text files</p>
        <input ref={fileInputRef} type="file" accept=".txt,.csv,.md,.pdf,.docx,.xlsx,.xls,.pptx,.ppt,.png,.jpg,.jpeg,.gif,.bmp,.webp" style={{ display: 'none' }} onChange={handleFileUpload} data-testid="file-input" />
      </div>

      {/* Text Upload Toggle */}
      <div style={{ marginBottom: 24 }}>
        <button className="btn-secondary" onClick={() => setShowTextForm(!showTextForm)} data-testid="toggle-text-upload"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <Plus size={16} />{showTextForm ? 'Hide Text Upload' : 'Paste Text Content'}
        </button>
      </div>

      {/* Text Upload Form */}
      {showTextForm && (
        <div className="text-upload-form slide-up">
          <div className="form-group">
            <label>Document Name</label>
            <input data-testid="doc-name" value={docName} onChange={e => setDocName(e.target.value)} placeholder="e.g., Remote Work Policy" />
          </div>
          <div className="form-group">
            <label>Tags (comma-separated)</label>
            <input data-testid="doc-tags" value={tags} onChange={e => setTags(e.target.value)} placeholder="e.g., policy, remote work, hr" />
          </div>
          <div className="form-group">
            <label>Content</label>
            <textarea data-testid="doc-content" value={textContent} onChange={e => setTextContent(e.target.value)}
              placeholder="Paste your document content here..." />
          </div>
          <button className="btn-action" onClick={handleTextUpload} disabled={uploading || !textContent.trim() || !docName.trim()} data-testid="submit-text-upload">
            {uploading ? 'Uploading...' : 'Upload Document'}
          </button>
        </div>
      )}

      {/* Document List */}
      <div className="section-header">
        <h3>Uploaded Documents ({documents.length})</h3>
      </div>
      {documents.length === 0 ? (
        <div className="empty-state">
          <FileText size={64} />
          <h4>No Documents Yet</h4>
          <p>Upload company handbooks, policies, or any HR documents to give your AI employees the context they need.</p>
        </div>
      ) : (
        <div className="doc-list">
          {documents.map(doc => (
            <div key={doc.id} className="doc-item" data-testid={`doc-${doc.id}`}>
              <div className="doc-item-info">
                <div className="doc-icon"><FileText size={20} /></div>
                <div>
                  <h4>{doc.name}</h4>
                  <div className="doc-meta">
                    {doc.file_type} · {doc.chunk_count} chunks · {new Date(doc.uploaded_at).toLocaleDateString()}
                  </div>
                  {doc.tags?.length > 0 && (
                    <div className="doc-tags">
                      {doc.tags.map(t => <span key={t} className="doc-tag"><Tag size={10} style={{ marginRight: 2 }} />{t}</span>)}
                    </div>
                  )}
                </div>
              </div>
              <button className="btn-delete" onClick={() => handleDelete(doc.id)} data-testid={`delete-doc-${doc.id}`}>
                <Trash2 size={14} style={{ marginRight: 4 }} />Delete
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
